import {FormControl, FormGroup} from '@angular/forms';
import {SelectableItem} from '@brunata-angular/ui';
import {filterNullish, includesIgnoreCase} from '@brunata-angular/util';
import {outputFormatLabel} from '../shared/models/output-format.model';
import {SavedExport, savedExportScheduleLabelKeys} from './saved-export.model';

export type SavedExportFilterForm = FormGroup<{
    searchTerm: FormControl<string>;
    outputFormats: FormControl<SelectableItem[]>;
    schedules: FormControl<SelectableItem[]>;
}>;

export type SavedExportFilterValues = {
    searchTerm: string;
    outputFormats: string[];
    schedules: string[];
};

export const emptySavedExportFilterValues: SavedExportFilterValues = {
    searchTerm: '',
    outputFormats: [],
    schedules: [],
};

export type SavedExportFilterIds = {
    outputFormat: string;
    schedule: string;
};

export type SavedExportFilterOption = {
    id: string;
    label: string;
};

export type SavedExportFilterOptions = {
    outputFormats: SavedExportFilterOption[];
    schedules: SavedExportFilterOption[];
};

export const emptySavedExportFilterOptions: SavedExportFilterOptions = {
    outputFormats: [],
    schedules: [],
};

export function savedExportFilterIds(savedExport: SavedExport): SavedExportFilterIds {
    return {
        outputFormat: savedExport.outputFormat,
        schedule: savedExport.scheduleLabelKey,
    };
}

export function savedExportFilterOptions(
    savedExports: SavedExport[],
    scheduleLabel: (scheduleLabelKey: string) => string,
): SavedExportFilterOptions {
    return {
        outputFormats: [...new Set(savedExports.map(savedExport => savedExport.outputFormat))]
            .sort()
            .map(outputFormat => ({
                id: outputFormat,
                label: outputFormatLabel(outputFormat) ?? outputFormat,
            })),
        schedules: savedExportScheduleLabelKeys
            .filter(labelKey =>
                savedExports.some(savedExport => savedExport.scheduleLabelKey === labelKey),
            )
            .map(labelKey => ({id: labelKey, label: scheduleLabel(labelKey)})),
    };
}

export function toSelectableItems(
    options: SavedExportFilterOption[],
    selectedIds: string[],
): SelectableItem[] {
    const selected = new Set(selectedIds);
    return options.map(option => ({name: option.label, selected: selected.has(option.id)}));
}

export function selectedOptionIds(
    options: SavedExportFilterOption[],
    items: SelectableItem[],
): string[] {
    const idsByLabel = new Map(options.map(option => [option.label, option.id]));
    return items
        .filter(item => item.selected)
        .map(item => idsByLabel.get(item.name))
        .filter(filterNullish);
}

export function deselectAll(items: SelectableItem[]): SelectableItem[] {
    return items.map(item => ({...item, selected: false}));
}

export function matchesSavedExportFilters(
    savedExport: SavedExport,
    ids: SavedExportFilterIds,
    {searchTerm, outputFormats, schedules}: SavedExportFilterValues,
): boolean {
    return (
        includesIgnoreCase(savedExport.name, searchTerm) &&
        matchesSelection(ids.outputFormat, outputFormats) &&
        matchesSelection(ids.schedule, schedules)
    );
}

function matchesSelection(id: string | null, selectedIds: string[]): boolean {
    return selectedIds.length === 0 || id === null || selectedIds.includes(id);
}
